// The call panel (REQ-001, REQ-016, REQ-017; docs/ringcentral/SOFTPHONE.md): a pill in the top header on every screen
// that drops the phone down below the header at the right edge, so a call carries on and stays in reach while the
// operator moves around the console without covering anything at the bottom of the page. Incoming calls with their screen pop,
// in-call controls (mute, hold, keypad, blind and warm transfer, hang up), a dial pad with caller ID, recent and missed
// calls with call back, and audio devices. Every action shows what the phone actually did: an action the provider
// refused shows its reason, never a success it didn't have.
//
// While an event is open the phone stays out of its way: a call placed from the event keeps its controls on the
// contact row (CallControls) and leaves the panel shut, and a ringing call shows in the header with Answer and Decline
// (RingStrip) instead of opening the panel over the event. The panel opens when the agent clicks the pill, and closes
// on a click outside it or Escape.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, errorText, type CommCall, type ScreenPop, type SoftphoneConfigResponse, type Workstation, type WorkstationHere, type WorkstationSeat } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { fmtPhone } from '../console/util.ts';
import { useStreamEvent } from '../stream.tsx';
import { AUDIO_IN, AUDIO_OUT, audioDevices } from './audio.ts';
import { DTMF, liveCalls, type ControlAction, type ControlArgs, type PhoneCall } from './engine.ts';
import { useEventInView, useSoftphoneState, type Softphone } from './softphone.ts';

function useTick(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return now;
}

const dur = (fromIso: string | null, now: number) => {
  if (!fromIso) return '0:00';
  const s = Math.max(0, Math.floor((now - Date.parse(fromIso)) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const END_TEXT: Record<string, string> = {
  Rejected: 'declined',
  Voicemail: 'sent to voicemail',
  BlindTransfer: 'transferred',
  AttendedTransfer: 'transferred',
  Cancelled: 'cancelled',
  NoAnswer: 'no answer',
  CallerInputRedirect: 'hung up',
  AnsweredElsewhere: 'answered on the desk phone',
  StoppedRinging: 'stopped ringing here',
  Failed: 'failed',
};

/** Ends on this phone that are not the call ending: it went to another device (or the server will say what happened). */
const MOVED = new Set(['AnsweredElsewhere', 'StoppedRinging']);

/** The screen pop for a call offered to this phone: who it may be, their sites and open incidents (REQ-016). */
function ScreenPopView({ telephonySessionId }: { telephonySessionId: string }) {
  const [pop, setPop] = useState<ScreenPop | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const ac = new AbortController();
    let tries = 0;
    const load = async () => {
      try {
        const s = await api.softphoneSession(telephonySessionId, ac.signal);
        setPop(await api.screenPop(s.communicationId, ac.signal));
      } catch (e) {
        if (ac.signal.aborted) return;
        // The call may not be on record yet (the provider's notification is a moment behind the phone).
        if (++tries < 6) setTimeout(() => void load(), 700);
        else setErr(errorText(e));
      }
    };
    void load();
    return () => ac.abort();
  }, [telephonySessionId]);
  if (err) return <div className="small muted">Caller details unavailable: {err}</div>;
  if (!pop) return <div className="small muted">Looking up the caller…</div>;
  const top = pop.candidates[0];
  const events = pop.candidates.flatMap((c) => c.openEvents);
  return (
    <div className="ppop">
      <div className="ppop-who">
        <b>{pop.caller.label}</b>
        {pop.caller.verified ? <span className="pill ok tiny-pill">verified</span> : pop.caller.identified ? <span className="pill warn tiny-pill">not verified</span> : null}
      </div>
      {top?.companyName && <div className="small">{top.companyName}</div>}
      {top?.sites.length ? <div className="small muted">Site: {top.sites.map((s) => s.name ?? s.siteId).join(', ')}</div> : null}
      {pop.candidates.length > 1 && <div className="small muted">{pop.candidates.length} possible matches: confirm who is calling</div>}
      {events.slice(0, 3).map((e) => (
        <a key={e.eventId} className="ppop-ev" href={`#/events/${e.eventId}`}>
          <Icon name="alert" /> {e.displayNo} · {e.severity} · {e.state}
          {e.ownerName ? ` · ${e.ownerName}` : ''}
        </a>
      ))}
      {pop.lastOutboundCall?.agentName && <div className="small muted">Last called by {pop.lastOutboundCall.agentName}</div>}
      <div className="small muted">A matching number is not proof of identity: verify before acting on what the caller says.</div>
    </div>
  );
}

/**
 * In-call controls for one call on this phone: mute, hold, keypad (DTMF, for gate codes and IVRs), blind or warm
 * transfer, and the consultation's complete and cancel. In the phone panel's call card, and compact (without transfer,
 * which is in the panel) on an event's contact row while the call is live there. Hang up is the caller's (each place has its own).
 */
export function CallControls({ sp, call, all, compact = false }: { sp: Softphone; call: PhoneCall; all: PhoneCall[]; compact?: boolean }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [panel, setPanel] = useState<'none' | 'keys' | 'transfer'>('none');
  const [target, setTarget] = useState('');
  const [digits, setDigits] = useState('');
  const act = async (action: ControlAction, args: ControlArgs = {}) => {
    setBusy(action);
    setErr(null);
    try {
      await sp.control(call.id, action, args);
      if (action === 'transfer' || action === 'warm_transfer_start') setPanel('none');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const consult = all.find((c) => c.consultFor === call.id && c.state !== 'ended');
  const answered = call.state === 'connected' || call.state === 'held';
  const transferTo = target.trim();
  return (
    <div className={`pcall-ctl${compact ? ' compact' : ''}`}>
      {answered && !call.consultFor && (
        <div className="pcall-row" role="group" aria-label="Call controls">
          <button type="button" className={`btn sm${call.muted ? ' on' : ''}`} disabled={!!busy || call.state === 'held'} onClick={() => void act(call.muted ? 'unmute' : 'mute')} aria-pressed={call.muted}>
            <Icon name="mute" /> {call.muted ? 'Unmute' : 'Mute'}
          </button>
          <button type="button" className={`btn sm${call.state === 'held' ? ' on' : ''}`} disabled={!!busy || !!consult} onClick={() => void act(call.state === 'held' ? 'unhold' : 'hold')} aria-pressed={call.state === 'held'}>
            <Icon name="pause" /> {call.state === 'held' ? 'Resume' : 'Hold'}
          </button>
          <button type="button" className={`btn sm${panel === 'keys' ? ' on' : ''}`} disabled={call.state === 'held'} onClick={() => setPanel(panel === 'keys' ? 'none' : 'keys')} aria-expanded={panel === 'keys'}>
            <Icon name="keyboard" /> Keypad
          </button>
          {/* Transfer doesn't fit an event's contact row cleanly; it is in the phone panel (click the pill). */}
          {!compact && (
            <button type="button" className={`btn sm${panel === 'transfer' ? ' on' : ''}`} disabled={!!consult} onClick={() => setPanel(panel === 'transfer' ? 'none' : 'transfer')} aria-expanded={panel === 'transfer'}>
              <Icon name="route" /> Transfer
            </button>
          )}
        </div>
      )}

      {answered && panel === 'keys' && (
        <form
          className="pcall-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (!DTMF.test(digits)) return setErr('Keys are 0-9, * and #');
            void act('dtmf', { digits }).then(() => setDigits(''));
          }}
        >
          <input aria-label="Keys to send" className="mono" value={digits} onChange={(e) => setDigits(e.target.value.toUpperCase())} placeholder="e.g. 1#" maxLength={32} autoComplete="off" autoFocus={compact} />
          <button type="submit" className="btn sm" disabled={!!busy || !digits}>
            Send
          </button>
        </form>
      )}

      {answered && panel === 'transfer' && (
        <div className="pcall-xfer">
          <input aria-label="Transfer to (number or extension)" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Number or extension" autoComplete="off" />
          <div className="pcall-row">
            <button type="button" className="btn sm" disabled={!!busy || !transferTo} onClick={() => void act('transfer', { to: transferTo })} title="Hand the call over now (blind)">
              Transfer now
            </button>
            <button type="button" className="btn sm" disabled={!!busy || !transferTo} onClick={() => void act('warm_transfer_start', { to: transferTo })} title="Hold the caller and talk to the target first (warm)">
              Talk first
            </button>
          </div>
        </div>
      )}

      {call.consultFor && answered && (
        <div className="pcall-row">
          <button type="button" className="btn sm okb" disabled={!!busy} onClick={() => void act('warm_transfer_complete')}>
            Complete transfer
          </button>
          <button type="button" className="btn sm" disabled={!!busy} onClick={() => void act('warm_transfer_cancel')}>
            Cancel, back to caller
          </button>
        </div>
      )}
      {call.consultFor && call.state === 'ringing' && (
        <div className="pcall-row">
          <button type="button" className="btn sm" disabled={!!busy} onClick={() => void act('warm_transfer_cancel')}>
            Cancel, back to caller
          </button>
        </div>
      )}
      {busy && <div className="small muted">Waiting for the phone…</div>}
      {err && (
        <div className="small bad-t" role="alert">
          Not done: {err}
        </div>
      )}
    </div>
  );
}

function CallCard({ sp, call, all, simulated, deskPhone, seatLabel }: { sp: Softphone; call: PhoneCall; all: PhoneCall[]; simulated: boolean; deskPhone: boolean; seatLabel: string | null }) {
  const now = useTick(call.state === 'connected' || call.state === 'held' || call.state === 'ringing');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const act = async (action: ControlAction) => {
    setBusy(action);
    setErr(null);
    try {
      await sp.control(call.id, action);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const number = call.remote ? fmtPhone(call.remote) : 'Unknown number';
  const live = call.state !== 'ended';

  return (
    <div className={`pcall ${call.state}`} aria-live="polite">
      <div className="pcall-head">
        <span className={`hdot ${call.state === 'ended' ? 'off' : call.state === 'ringing' ? 'unk' : 'on'}`} aria-hidden="true" />
        <span className="pcall-what">
          {call.consultFor
            ? 'Transfer consultation'
            : call.state === 'ringing'
              ? call.direction === 'inbound'
                ? 'Incoming call'
                : 'Calling…'
              : call.state === 'held'
                ? 'On hold'
                : call.state === 'ended'
                  ? MOVED.has(call.endReason ?? '')
                    ? `No longer on this phone · ${END_TEXT[call.endReason!]}`
                    : `Call ended${call.endReason ? ` · ${END_TEXT[call.endReason] ?? call.endReason}` : ''}`
                  : call.direction === 'inbound'
                    ? 'Inbound call'
                    : 'Outbound call'}
        </span>
        <span className="sp" />
        {simulated && <span className="pill tiny-pill">simulated</span>}
        {live && <span className="mono small">{dur(call.answeredAt ?? call.startedAt, now)}</span>}
      </div>
      <div className="pcall-num mono">{number}</div>
      {call.line ? <div className="small muted">on {fmtPhone(call.line)}</div> : seatLabel ? <div className="small muted">on the {seatLabel} phone</div> : null}
      {call.state === 'ringing' && call.direction === 'inbound' && call.telephonySessionId && <ScreenPopView telephonySessionId={call.telephonySessionId} />}
      {call.muted && live && <span className="pill warn tiny-pill">muted</span>}

      {call.state === 'ringing' && call.direction === 'inbound' && (
        <div className="pcall-row">
          <button type="button" className="btn okb" disabled={!!busy} onClick={() => void act('answer')}>
            <Icon name="phone" /> Answer
          </button>
          <button type="button" className="btn badb" disabled={!!busy} onClick={() => void act('decline')}>
            <Icon name="x" /> Decline
          </button>
          {simulated && deskPhone && call.telephonySessionId && (
            <button
              type="button"
              className="btn sm"
              disabled={!!busy}
              title="The workstation's desk phone rings too: pick it up there (simulated here)"
              onClick={() => void api.simAnswerOnDeskPhone(call.telephonySessionId!).catch((e: unknown) => setErr(errorText(e)))}
            >
              Answer on desk phone
            </button>
          )}
        </div>
      )}

      <CallControls sp={sp} call={call} all={all} />

      {live && !(call.state === 'ringing' && call.direction === 'inbound') && !call.consultFor && (
        <div className="pcall-row">
          <button type="button" className="btn badb" disabled={!!busy} onClick={() => void act('hangup')}>
            <Icon name="x" /> Hang up
          </button>
        </div>
      )}
      {busy && <div className="small muted">Waiting for the phone…</div>}
      {err && (
        <div className="small bad-t" role="alert">
          Not done: {err}
        </div>
      )}
    </div>
  );
}

interface ServerCall {
  communicationId: string;
  telephonySessionId: string | null;
  direction: 'inbound' | 'outbound';
  status: string;
  onHold: boolean;
  remoteLast4: string | null;
  answeredAt: string | null;
}

/** One call the server has on record for this agent that the console's phone doesn't hold: on the desk phone (or another device). */
function ServerCallCard({ c, seat, onDone }: { c: ServerCall; seat: WorkstationSeat | null; onDone: () => void }) {
  const now = useTick(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [xfer, setXfer] = useState(false);
  const [to, setTo] = useState('');
  const act = async (action: 'hold' | 'unhold' | 'hangup' | 'transfer', target?: string) => {
    setBusy(true);
    setErr(null);
    try {
      await api.deskControl(c.communicationId, action, target);
      if (action === 'transfer') setXfer(false);
      onDone();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const answered = c.status === 'answered';
  return (
    <div className={`pcall ${c.onHold ? 'held' : answered ? 'connected' : 'ringing'}`}>
      <div className="pcall-head">
        <span className={`hdot ${answered ? 'on' : 'unk'}`} aria-hidden="true" />
        <span className="pcall-what">{seat ? `On the ${seat.label} desk phone` : 'On another device'}</span>
        <span className="sp" />
        {answered && <span className="mono small">{dur(c.answeredAt, now)}</span>}
      </div>
      <div className="pcall-num mono">···{c.remoteLast4 ?? '????'}</div>
      <div className="small muted">
        {c.onHold ? 'On hold' : answered ? 'Connected' : c.status}. Controlled from here through RingCentral; mute and keys are on the desk phone.
      </div>
      {answered && (
        <div className="pcall-row" role="group" aria-label="Desk phone call controls">
          <button type="button" className={`btn sm${c.onHold ? ' on' : ''}`} disabled={busy} onClick={() => void act(c.onHold ? 'unhold' : 'hold')} aria-pressed={c.onHold}>
            <Icon name="pause" /> {c.onHold ? 'Resume' : 'Hold'}
          </button>
          <button type="button" className={`btn sm${xfer ? ' on' : ''}`} disabled={busy} onClick={() => setXfer(!xfer)}>
            <Icon name="route" /> Transfer
          </button>
          <button type="button" className="btn sm badb" disabled={busy} onClick={() => void act('hangup')}>
            <Icon name="x" /> Hang up
          </button>
        </div>
      )}
      {answered && xfer && (
        <div className="pcall-row">
          <input aria-label="Transfer to (number or extension)" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Number or extension" autoComplete="off" />
          <button type="button" className="btn sm" disabled={busy || !to.trim()} onClick={() => void act('transfer', to.trim())}>
            Transfer now
          </button>
        </div>
      )}
      {busy && <div className="small muted">Waiting for RingCentral…</div>}
      {err && (
        <div className="small bad-t" role="alert">
          Not done: {err}
        </div>
      )}
    </div>
  );
}

/**
 * Live calls the server has on record for this agent that the console's phone doesn't hold (TEST-012): answered on the
 * workstation's desk phone, or the page reloaded or the network dropped. A call the phone has (or just ended) is not
 * one: the server's record follows the provider a moment later. Read again every few seconds while there are any.
 */
function ServerCalls({ sp, phoneCalls, seat }: { sp: Softphone; phoneCalls: PhoneCall[]; seat: WorkstationSeat | null }) {
  const [calls, setCalls] = useState<ServerCall[]>([]);
  const ids = phoneCalls.map((c) => `${c.telephonySessionId}:${c.state}`).join(',');
  const [again, setAgain] = useState(0);
  useEffect(() => {
    let live = true;
    void api
      .softphoneHttp('GET', '/api/softphone/calls')
      .then((r) => live && setCalls((r as { calls: ServerCall[] }).calls))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [ids, again]);
  // On this phone: live here, or just ended here (its record catches up in a moment). A call that left this phone for the
  // desk phone is not: the server's record shows it, with the desk phone's controls.
  const mine = new Set(phoneCalls.filter((c) => c.state !== 'ended' || !MOVED.has(c.endReason ?? '')).map((c) => c.telephonySessionId));
  const others = calls.filter((c) => !c.telephonySessionId || !mine.has(c.telephonySessionId));
  useEffect(() => {
    if (!others.length) return;
    const t = setTimeout(() => setAgain((n) => n + 1), 3000);
    return () => clearTimeout(t);
  }, [others.length, again]);
  if (!others.length) return null;
  return (
    <>
      {others.map((c) => (
        <ServerCallCard key={c.communicationId} c={c} seat={seat} onDone={() => setTimeout(() => setAgain((n) => n + 1), 400)} />
      ))}
      {!seat && (
        <div className="small muted">
          If one of these should be on this phone, the page may have reloaded or the network dropped: it is still live at the provider and ends only when the provider ends it.{' '}
          <button type="button" className="btn sm" onClick={() => sp.reconnect()}>
            Reconnect
          </button>
        </div>
      )}
    </>
  );
}

/** Which workstation the agent sits at (docs/ringcentral/WORKSTATIONS.md): its phone is theirs while seated. */
/** The console tells the server "I'm here" once per page load: on a recognised PC, the agent is seated at its desk. */
let hereSent = false;

function SeatBar({ seat, busyCall, onChange }: { seat: WorkstationSeat | null; busyCall: boolean; onChange: () => void }) {
  const [desks, setDesks] = useState<Workstation[] | null>(null);
  const [here, setHere] = useState<WorkstationHere | null>(null);
  const [pick, setPick] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [why, setWhy] = useState(false);
  useEffect(() => setWhy(false), [seat?.workstationId]);
  useEffect(() => {
    const ac = new AbortController();
    api.workstations(false, ac.signal).then(
      (r) => {
        setDesks(r.workstations);
        setHere(r.here);
      },
      () => setDesks([]),
    );
    return () => ac.abort();
  }, [seat?.workstationId, tick]);
  // Not seated: keep the list of desks (and who sits where) current.
  useEffect(() => {
    if (seat) return;
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [seat]);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      onChange();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  /** Sit at this PC's own desk (the same as when the console loads). */
  const sitHere = () =>
    run(async () => {
      const r = await api.workstationHere();
      if (r.outcome === 'refused') throw new Error(r.reason ?? 'not now');
      setTick((n) => n + 1);
    });
  if (!desks || (!seat && desks.length === 0)) return null;
  if (seat)
    return (
      <div className="pseat">
        <Icon name="phone" />
        <span>
          <b>{seat.label}</b>
          {seat.method === 'device' && <span className="small muted"> · this computer</span>}
          <span className="small muted"> · since {new Date(seat.since).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
        </span>
        <span className="sp" />
        {seat.method === 'device' && (
          <button type="button" className="btn sm" onClick={() => setWhy(!why)} aria-expanded={why}>
            Not right?
          </button>
        )}
        <button type="button" className="btn sm" disabled={busy || busyCall} title={busyCall ? 'Finish the call first' : 'Leave this workstation (its calls stop coming to you)'} onClick={() => void run(() => api.leaveWorkstation())}>
          Leave desk
        </button>
        {why && (
          <div className="small">
            This computer{here?.deviceName ? ` (${here.deviceName})` : ''} is registered as <b>{seat.label}</b>, so its phone rings here. If you are not at {seat.label}, tell a supervisor: an admin corrects it in Setup → Workstations.
          </div>
        )}
        {err && <div className="small bad-t">{err}</div>}
      </div>
    );
  // On a recognised PC: its own desk, one click (after "Leave desk", or when someone was on a call there).
  if (here?.workstationId)
    return (
      <div className="pseat">
        <Icon name="phone" />
        <span>
          This computer is <b>{here.label}</b>
        </span>
        <span className="sp" />
        <button type="button" className="btn sm pri" disabled={busy} onClick={() => void sitHere()}>
          Sit here
        </button>
        {err && <div className="small bad-t">{err}</div>}
      </div>
    );
  const free = desks.filter((d) => !d.seated && !d.ownComputer);
  return (
    <div className="pseat">
      <label className="small">
        Your workstation{' '}
        <select value={pick} onChange={(e) => setPick(e.target.value)}>
          <option value="">Pick the desk you sit at</option>
          {desks.map((d) => (
            <option key={d.id} value={d.id} disabled={!!d.seated || d.ownComputer}>
              {d.label}
              {d.seated ? ` (${d.seated.name})` : d.ownComputer ? ' (log in at its computer)' : ''}
            </option>
          ))}
        </select>
      </label>
      <button type="button" className="btn sm pri" disabled={busy || !pick || !free.some((d) => d.id === pick)} onClick={() => void run(() => api.claimWorkstation(pick))}>
        Sit here
      </button>
      {here?.blocked && <div className="small muted">This computer is blocked from taking a desk by itself.</div>}
      {err && <div className="small bad-t">{err}</div>}
    </div>
  );
}

function DialPad({ sp, cfg }: { sp: Softphone; cfg: SoftphoneConfigResponse | null }) {
  const [to, setTo] = useState('');
  const [callerId, setCallerId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dial = async () => {
    setBusy(true);
    setErr(null);
    try {
      await sp.dial(to.trim(), callerId || null);
      setTo('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="pdial"
      onSubmit={(e) => {
        e.preventDefault();
        void dial();
      }}
    >
      <input aria-label="Number to call" className="mono" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Number to call" autoComplete="off" />
      {cfg && cfg.callerIds.length > 0 && (
        <label className="small">
          Caller ID{' '}
          <select value={callerId} onChange={(e) => setCallerId(e.target.value)}>
            <option value="">Default line</option>
            {cfg.callerIds.map((n) => (
              <option key={n} value={n}>
                {fmtPhone(n)}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="pcall-row">
        <button type="submit" className="btn pri" disabled={busy || !to.trim()}>
          <Icon name="phone" /> Call
        </button>
        {sp.engine === 'simulator' && (
          <button type="button" className="btn sm" onClick={() => void api.softphoneTestCall().catch((e: unknown) => setErr(errorText(e)))} title="A simulated caller rings this phone">
            Test incoming call
          </button>
        )}
      </div>
      {err && (
        <div className="small bad-t" role="alert">
          {err}
        </div>
      )}
    </form>
  );
}

function CallList({ sp, which }: { sp: Softphone; which: 'mine' | 'missed' }) {
  const [calls, setCalls] = useState<CommCall[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const ac = new AbortController();
    api
      .phoneCalls(which, ac.signal)
      .then((r) => setCalls(r.calls.slice(0, 25)))
      .catch((e: unknown) => !ac.signal.aborted && setErr(errorText(e)));
    return () => ac.abort();
  }, [which]);
  const callBack = async (c: CommCall) => {
    setErr(null);
    try {
      const { to } = await api.softphoneRedial(c.id);
      await sp.dial(to);
    } catch (e) {
      setErr(errorText(e));
    }
  };
  if (err) return <div className="small bad-t">{err}</div>;
  if (!calls) return <div className="small muted">Loading…</div>;
  if (!calls.length) return <div className="small muted">{which === 'missed' ? 'No missed calls in the last 7 days.' : 'No calls in the last 7 days.'}</div>;
  return (
    <ul className="plist">
      {calls.map((c) => (
        <li key={c.id}>
          <span className={c.status === 'missed' ? 'bad-t' : undefined}>{c.direction === 'inbound' ? '↙' : '↗'}</span>
          <span className="plist-who">
            {c.contactName ?? `···${c.remoteLast4 ?? '????'}`}
            <span className="small muted">
              {' '}
              {new Date(c.startedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {c.status}
              {c.simulated ? ' · simulated' : ''}
            </span>
          </span>
          <button type="button" className="btn sm" onClick={() => void callBack(c)} title="Call this number back (the number is revealed to you and audited)">
            Call back
          </button>
        </li>
      ))}
    </ul>
  );
}

function AudioSettings({ simulated }: { simulated: boolean }) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [sel, setSel] = useState(audioDevices);
  const [err, setErr] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    try {
      setDevices(await navigator.mediaDevices.enumerateDevices());
    } catch (e) {
      setErr(errorText(e));
    }
  }, []);
  useEffect(() => {
    void refresh();
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', refresh);
  }, [refresh]);
  const allow = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
      await refresh();
    } catch (e) {
      setErr(`Microphone not allowed: ${errorText(e)}`);
    }
  };
  const pick = (key: string, v: string) => {
    try {
      if (v) localStorage.setItem(key, v);
      else localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    setSel(audioDevices());
  };
  const inputs = devices.filter((d) => d.kind === 'audioinput');
  const outputs = devices.filter((d) => d.kind === 'audiooutput');
  const named = inputs.some((d) => d.label);
  return (
    <div className="paudio">
      {simulated && <div className="small muted">Simulated calls have no audio; the devices picked here are used by the RingCentral phone.</div>}
      {!simulated && <div className="small muted">Used from the next call; the tab you are using plays the call.</div>}
      {!named && (
        <button type="button" className="btn sm" onClick={() => void allow()}>
          <Icon name="mic" /> Allow the microphone
        </button>
      )}
      <label className="small">
        Microphone{' '}
        <select value={sel.input ?? ''} onChange={(e) => pick(AUDIO_IN, e.target.value)}>
          <option value="">System default</option>
          {inputs.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || 'Microphone'}
            </option>
          ))}
        </select>
      </label>
      <label className="small">
        Speaker{' '}
        <select value={sel.output ?? ''} onChange={(e) => pick(AUDIO_OUT, e.target.value)} disabled={!outputs.length}>
          <option value="">System default</option>
          {outputs.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || 'Speaker'}
            </option>
          ))}
        </select>
      </label>
      {err && <div className="small bad-t">{err}</div>}
    </div>
  );
}

/**
 * A call coming in while the panel is shut (always so while an event is open), in the header row so the phone doesn't
 * cover the work (the header grows by this strip; nothing is covered). Ringing: Answer and Decline. Answered: how long, and Hang up. Details opens the
 * panel with the caller's screen pop and every control.
 */
function RingStrip({ sp, call }: { sp: Softphone; call: PhoneCall }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ringing = call.state === 'ringing';
  const now = useTick(!ringing);
  const act = async (action: 'answer' | 'decline' | 'hangup') => {
    setBusy(true);
    setErr(null);
    try {
      await sp.control(call.id, action);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="ring-strip" role="alert">
      <span className="ring-who">
        <b>{ringing ? 'Incoming call' : call.state === 'held' ? 'On hold' : 'On a call'}</b> <span className="mono">{call.remote ? fmtPhone(call.remote) : 'Unknown number'}</span>
        {!ringing && <span className="mono muted"> · {dur(call.answeredAt ?? call.startedAt, now)}</span>}
      </span>
      {ringing ? (
        <>
          <button type="button" className="btn sm okb" disabled={busy} onClick={() => void act('answer')}>
            <Icon name="phone" /> Answer
          </button>
          <button type="button" className="btn sm badb" disabled={busy} onClick={() => void act('decline')}>
            <Icon name="x" /> Decline
          </button>
        </>
      ) : (
        <button type="button" className="btn sm badb" disabled={busy} onClick={() => void act('hangup')}>
          <Icon name="x" /> Hang up
        </button>
      )}
      <button type="button" className="btn sm" onClick={() => sp.setOpen(true)} title="Open the phone: who may be calling, their sites and open incidents">
        Details
      </button>
      {err && <span className="small bad-t">Not done: {err}</span>}
    </span>
  );
}

/** Where the header ends on screen (it is sticky at the top and grows when it wraps), for the phone to drop down from. */
function useBelowHeader(slot: HTMLElement | null, on: boolean): number {
  const [y, setY] = useState(0);
  useEffect(() => {
    const top = slot?.closest('header');
    if (!top || !on) return;
    const read = () => setY(Math.round(top.getBoundingClientRect().bottom));
    read();
    const ro = new ResizeObserver(read);
    ro.observe(top);
    window.addEventListener('resize', read);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', read);
    };
  }, [slot, on]);
  return y;
}

/**
 * `slot` is the header's place for the phone pill (Header `phoneSlot`). The panel itself renders where it is mounted,
 * just after the header, above drawers, so calls stay in reach while one is open.
 */
export function CallPanel({ sp, slot }: { sp: Softphone; slot: HTMLElement | null }) {
  const st = useSoftphoneState(sp);
  const [tab, setTab] = useState<'dial' | 'recent' | 'missed' | 'audio'>('dial');
  const [cfg, setCfg] = useState<SoftphoneConfigResponse | null>(null);
  const [signInErr, setSignInErr] = useState<string | null>(null);
  const [cfgTick, setCfgTick] = useState(0);
  useStreamEvent('softphone', (d) => {
    sp.serverMessage(d);
    // The agent's workstation changed (here, in another tab, or a supervisor moved them): read the phone's setup again.
    if ((d as { type?: string } | null)?.type === 'seat') setCfgTick((n) => n + 1);
  });
  useEffect(() => {
    sp.load();
  }, [sp]);
  // Workstation recognition (docs/ringcentral/WORKSTATION-DEVICES.md): on a SOC PC assigned to a desk, logging in seats
  // the agent there. Elsewhere nothing changes and the agent picks their desk.
  useEffect(() => {
    if (hereSent) return;
    hereSent = true;
    api.workstationHere().then((r) => {
      if (r.outcome === 'seated') setCfgTick((n) => n + 1);
    }, () => undefined);
  }, []);
  useEffect(() => {
    const ac = new AbortController();
    api.softphoneConfig(ac.signal).then(setCfg, () => undefined);
    return () => ac.abort();
  }, [sp, cfgTick]);
  const seat = cfg?.seat ?? null;
  useEffect(() => {
    document.body.classList.add('has-softphone');
    return () => document.body.classList.remove('has-softphone');
  }, []);
  const calls = st.phone.calls;
  const live = liveCalls(st.phone);
  const ringingCall = live.find((c) => c.state === 'ringing' && c.direction === 'inbound') ?? null;
  const ringing = !!ringingCall;
  const inEvent = useEventInView();
  // The call shown in the header strip whenever the panel is shut: one ringing in, else one that came in and was
  // answered. So shutting the panel (a click outside, Escape) never hides a call; an event's own calls are on their
  // contact's row.
  const stripCall = ringingCall ?? live.find((c) => c.direction === 'inbound' && !c.consultFor && c.state !== 'ringing') ?? null;
  // A call ringing opens the panel, except over an event: there it rings in the header (RingStrip).
  const wasRinging = useRef(false);
  useEffect(() => {
    if (ringing && !wasRinging.current && !st.open && !inEvent) sp.setOpen(true);
    wasRinging.current = ringing;
  }, [ringing, st.open, sp, inEvent]);
  // An open panel closes on a click outside it (or on its pill) and on Escape, so it never stays stuck over the work.
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!st.open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (t && (panelRef.current?.contains(t) || toggleRef.current?.contains(t))) return;
      sp.setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') sp.setOpen(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [st.open, sp]);
  const now = useTick(live.length > 0);

  const reg = st.phone.registration;
  // The header pill's colour, like presence and Live beside it: green ready, amber connecting, red needs attention.
  const tone = reg === 'registered' ? 'ph-on' : reg === 'starting' || reg === 'reconnecting' ? 'ph-wait' : reg === 'error' || reg === 'needs_sign_in' ? 'ph-bad' : 'ph-off';
  const text =
    reg === 'registered'
      ? ringing
        ? 'incoming call'
        : live.length === 1
          ? `1 call · ${dur(live[0]!.answeredAt ?? live[0]!.startedAt, now)}`
          : live.length
            ? `${live.length} calls`
            : 'ready'
      : reg === 'needs_sign_in'
        ? 'sign in'
        : reg === 'starting'
          ? 'connecting…'
          : reg === 'reconnecting'
            ? 'reconnecting…'
            : reg === 'error'
              ? 'unavailable'
              : 'off';

  const signIn = async () => {
    setSignInErr(null);
    try {
      await sp.signIn();
      setCfg(await api.softphoneConfig());
    } catch (e) {
      setSignInErr(errorText(e));
    }
  };

  const below = useBelowHeader(slot, st.open);
  const collapseTitle = 'Collapse the phone (calls carry on)';
  // "Phone", plus the state when it isn't simply ready ("Phone · 1 call", "Phone · sign in"); the desk and provider
  // are in the tooltip and the panel.
  const toggle = (
    <button
      ref={toggleRef}
      type="button"
      className={`status phone-toggle ${tone}${ringing ? ' ringing' : ''}`}
      onClick={() => sp.setOpen(!st.open)}
      aria-expanded={st.open}
      aria-controls="phone-panel"
      title={`${seat ? `${seat.label} phone` : sp.label} · ${text}. ${st.open ? collapseTitle : 'Show the phone'}`}
    >
      <i aria-hidden="true" />
      Phone{text === 'ready' ? '' : ` · ${text}`}
      <Icon name={st.open ? 'up' : 'down'} />
    </button>
  );

  return (
    <>
      {slot &&
        createPortal(
          <>
            {toggle}
            {stripCall && !st.open && <RingStrip key={stripCall.id} sp={sp} call={stripCall} />}
          </>,
          slot,
        )}
      {st.open && (
        <div
          ref={panelRef}
          id="phone-panel"
          className="phone-body"
          role="region"
          aria-label={`${sp.label} phone`}
          style={below ? { top: below + 6, maxHeight: `calc(100vh - ${below + 18}px)` } : undefined}
        >
          {/* The header pill sits under an open drawer's scrim; this keeps the phone collapsible from here. */}
          <div className="phone-head">
            <span className="small muted">{seat ? `${seat.label} phone` : sp.label}</span>
            <button type="button" className="btn sm icon" onClick={() => sp.setOpen(false)} title={collapseTitle} aria-label={collapseTitle}>
              <Icon name="up" />
            </button>
          </div>
          {reg === 'needs_sign_in' && (
            <div className="banner warnish" role="alert">
              <Icon name="phone" />
              <span>
                <b>Sign in to RingCentral</b> as yourself to take and place calls here.{cfg && !cfg.hasExtension ? ' You have no RingCentral extension in the SOC yet: ask an admin (Setup → Users).' : ''}
              </span>
              <button type="button" className="btn sm pri" onClick={() => void signIn()} disabled={!!cfg && !cfg.hasExtension}>
                Sign in
              </button>
            </div>
          )}
          {signInErr && <div className="small bad-t">{signInErr}</div>}
          {reg === 'error' && (
            <div className="banner warnish" role="alert">
              <Icon name="alert" />
              <span>
                <b>Phone unavailable.</b> {st.error} Dial from a desk phone and record the outcome.
              </span>
              <button type="button" className="btn sm" onClick={() => sp.reconnect()}>
                Try again
              </button>
            </div>
          )}
          {reg === 'reconnecting' && <div className="small muted">Reconnecting the phone; calls in progress carry on.</div>}
          <SeatBar key={String(st.open)} seat={seat} busyCall={live.length > 0} onChange={() => setCfgTick((n) => n + 1)} />
          {reg === 'registered' && <ServerCalls sp={sp} phoneCalls={calls} seat={seat} />}
          {calls.map((c) => (
            <CallCard key={c.id} sp={sp} call={c} all={calls} simulated={st.phone.simulated} deskPhone={!!seat} seatLabel={seat?.label ?? null} />
          ))}
          {reg === 'registered' && live.length === 0 && (
            <>
              <div className="seg full" role="tablist">
                {(['dial', 'recent', 'missed', 'audio'] as const).map((t) => (
                  <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? 'on' : undefined} onClick={() => setTab(t)}>
                    {t === 'dial' ? 'Dial' : t === 'recent' ? 'Recent' : t === 'missed' ? 'Missed' : 'Audio'}
                  </button>
                ))}
              </div>
              {tab === 'dial' && <DialPad sp={sp} cfg={cfg} />}
              {tab === 'recent' && <CallList sp={sp} which="mine" />}
              {tab === 'missed' && <CallList sp={sp} which="missed" />}
              {tab === 'audio' && <AudioSettings simulated={st.phone.simulated} />}
            </>
          )}
          {sp.engine === 'ringcentral' && reg === 'registered' && live.length === 0 && (
            <button type="button" className="btn sm phone-signout" onClick={() => void sp.signOut().catch((e: unknown) => setSignInErr(errorText(e)))}>
              Sign out of RingCentral
            </button>
          )}
        </div>
      )}
    </>
  );
}
